/** Read-only loaded geometry accounting. Counts instances, never deduplicates
 * shared asset geometry into a misleading rendering-cost reduction. */
import { Material } from '@babylonjs/core/Materials/material';
import type { SceneObject, TraceScene } from '../../exports/scene';

export function geometryCost(scene: TraceScene, root?: SceneObject) {
    const branches = new Map<SceneObject, { path: string; triangles: number; meshInstances: number }>();
    let sceneObjectCount = 0, triangles = 0, meshInstances = 0, bevelMeshInstances = 0, trianglesOnBevelObjects = 0, skippedMeshes = 0, skippedHierarchies = 0;
    const seen = new Set<unknown>();
    for (const object of scene.getSceneObjects()) {
        let branch = object, ancestor: SceneObject | null = object;
        const chain = new Set<SceneObject>();
        let invalidHierarchy = false;
        while (ancestor && ancestor !== root) {
            if (chain.has(ancestor) || chain.size >= 512) { skippedHierarchies++; invalidHierarchy = true; break; }
            chain.add(ancestor); branch = ancestor; ancestor = ancestor.getParent();
        }
        if (invalidHierarchy || (root && ancestor !== root)) continue;
        sceneObjectCount++;
        const bevel = Boolean(object.getBehavior('Bevel'));
        for (const mesh of object.getOwnedMeshes()) {
            if (seen.has(mesh) || !mesh.isEnabled() || !mesh.isVisible || mesh.visibility <= 0 || !mesh.getTotalVertices()) continue;
            seen.add(mesh);
            const indices = mesh.getTotalIndices();
            const unsupported = ('thinInstanceCount' in mesh && Number(mesh.thinInstanceCount) > 0)
                || mesh.subMeshes.some(s => {
                    const material = s.getMaterial();
                    return material && ![Material.TriangleFillMode, Material.WireFrameFillMode, Material.PointFillMode].includes(('overrideRenderingFillMode' in mesh && typeof mesh.overrideRenderingFillMode === 'number' ? mesh.overrideRenderingFillMode : material.fillMode));
                });
            if (!indices || indices % 3 || unsupported) { skippedMeshes++; continue; }
            const count = indices / 3;
            triangles += count; meshInstances++;
            if (bevel) { trianglesOnBevelObjects += count; bevelMeshInstances++; }
            let row = branches.get(branch);
            if (!row) { row = { path: branch.getPath(), triangles: 0, meshInstances: 0 }; branches.set(branch, row); }
            row.triangles += count; row.meshInstances++;
        }
    }
    const rows = [...branches.values()].sort((a, b) => b.triangles - a.triangles || a.path.localeCompare(b.path));
    return {
        method: 'SceneObject count includes the selected root and empty/inactive descendants (the whole scene when no root is selected); invalid hierarchies are excluded and counted by skippedHierarchies. Not physical pieces or a guarantee of clone success. Loaded indexed triangle instances on enabled, visible owned meshes, including descendants. Shared geometry counts once per instance. Not draw calls, unique storage or a GPU benchmark; thin instances/unindexed/non-triangle data are skipped. Bevel association is measured, not a savings estimate.',
        sceneObjectCount, triangles, meshInstances, bevelMeshInstances, trianglesOnBevelObjects, skippedMeshes, skippedHierarchies,
        largestBranches: rows.slice(0, 5), omittedBranches: Math.max(0, rows.length - 5),
        branchScope: root ? 'Direct-child subtrees plus any geometry owned by the selected object itself. Inspect a listed path to drill down.' : 'Top-level subtrees.',
    };
}
